/**
 * „Wieder da“ in der Mehrfachauswahl (Rückmeldungen 4.1): die gescannten
 * Geräte an ihren Stammplatz.
 *
 * Erst eine Übersicht mit allen geplanten Buchungen, einzelne lassen sich
 * abwählen. Was abgewählt ist oder nicht gebucht werden kann, bleibt als Rest
 * in der Sammelliste. Geräte mit einem Status, der die Stammplatz-Rückbuchung
 * ignoriert (defekt, in Prüfung), bleiben liegen -- dieselbe Regel wie bei
 * „Alles da“ (ADR 0025).
 */

import { bestandLaden, type Bestand } from "./bestand.js";
import { verboten, type Gebucht, neuerVorgang, verschieben } from "./verschieben.js";

export interface WiederDaPlan {
  /** Was zurückgebucht wird, in der Reihenfolge der Sammelliste. */
  zeilen: { artikel_id: string; stammplatz_id: string }[];
  /** Liegt schon an seinem Stammplatz. */
  schonDa: string[];
  /** Hat keinen Stammplatz, oder er ist stillgelegt. */
  ohneStammplatz: string[];
  /** Bleibt wegen seines Status liegen. */
  liegenLassen: string[];
}

/** Was „Wieder da“ mit diesen Geräten täte. */
export function wiederDaPlan(bestand: Bestand, ids: readonly string[]): WiederDaPlan {
  // Eine lokale Kopie von vor Vertrag 2.10 kennt den Haken nicht (ADR 0025).
  const ignorieren = new Set(
    bestand.statusArten
      .filter((a) => (a.rueckbuchung_ignorieren as boolean | undefined) === true)
      .map((a) => a.schluessel),
  );
  const plan: WiederDaPlan = { zeilen: [], schonDa: [], ohneStammplatz: [], liegenLassen: [] };

  for (const id of ids) {
    const stammplatz = bestand.artikel.get(id)?.stammplatz_id ?? null;
    const ort = stammplatz === null ? undefined : bestand.lagerorte.get(stammplatz);
    if (stammplatz === null || ort === undefined || !ort.aktiv) {
      plan.ohneStammplatz.push(id);
      continue;
    }
    const z = bestand.zustaende.get(id);
    if (z !== undefined && z.verbleib !== "nicht_erfasst" && z.lagerort_id === stammplatz) {
      plan.schonDa.push(id);
      continue;
    }
    if (z !== undefined && ignorieren.has(z.status)) {
      plan.liegenLassen.push(id);
      continue;
    }
    if (verboten(bestand, id, { art: "lagerort", id: stammplatz })) {
      plan.ohneStammplatz.push(id);
      continue;
    }
    plan.zeilen.push({ artikel_id: id, stammplatz_id: stammplatz });
  }
  return plan;
}

/**
 * Bucht die gewählten Zeilen -- ein Vorgang, „Rückgängig“ für alle. Die Kisten
 * zuerst: Kommt eine Kiste zurück, landet ihr Inhalt danach in einer Kiste,
 * die schon wieder dort steht.
 */
export async function wiederDaBuchen(
  bestand: Bestand,
  plan: WiederDaPlan,
  gewaehlt: ReadonlySet<string>,
): Promise<Gebucht[]> {
  const kisten = new Set<string>();
  for (const l of bestand.lagerorte.values()) if (l.artikel_id !== null) kisten.add(l.artikel_id);
  const reihe = plan.zeilen
    .filter((z) => gewaehlt.has(z.artikel_id))
    .sort((a, b) => Number(kisten.has(b.artikel_id)) - Number(kisten.has(a.artikel_id)));

  const vorgang = neuerVorgang();
  const gebucht: Gebucht[] = [];
  for (const z of reihe) {
    gebucht.push(
      // Frisch gelesen: Die Kiste davor kann schon zurueck sein.
      await verschieben(
        await bestandLaden(),
        z.artikel_id,
        { art: "lagerort", id: z.stammplatz_id },
        vorgang,
      ),
    );
    vorgang.stelle += 1;
  }
  return gebucht;
}
